import React, { useState, useEffect } from "react";
import { useAuth } from "../../Auth/AuthProvider";
import "../../CSS/navbar.css";
import { useTranslation } from "react-i18next";
import {
  BacklogIcon2,
  CheckInIcon,
  CheckOutIcon,
  DriverWheel,
  InventoryIcon,
  LogoutIcon,
  OpenSidebarIcon,
  OverviewIcon,
  ReportsIcon,
  SettingsIcon,
  WrenchIcon,
} from "../../Media/Icons";
import { Logo } from "../../Media/logos";
import { MobileNavCategory, NavCategory } from "./Category";
import { NavRoute } from "./NavRoute";
import { useWindowWidth } from "../../Hooks/useWindowWidth";
import { IsUserRole } from "./NavFunctions";

export const NewSideBar2 = () => {
  const [isAuth, setIsAuth] = useState(false);
  const { user, isSideOpen, openSideBar } = useAuth();
  const [openCategory, setOpenCategory] = useState("");
  const [filteredRoutes, setFilteredRoutes] = useState([]);
  const width = useWindowWidth();

  const isTablet = width <= 1300 && width > 500;
  const isMobile = width <= 500;

  const { t, i18n } = useTranslation();

  const userHasAccess = (roles, userGroups) => {
    return roles.some((role) =>
      userGroups.some((group) => group.name === role),
    );
  };

  const navRoutes = [
    {
      category: t("page-headers.overview"),
      roles: [
        "Admin",
        "Guard",
        "Guard Supervisor",
        "Safety",
        "Dispatch",
        "Mechanic",
        "NewMechanic",
      ],
      icon: <OverviewIcon />,
      routes: [
        {
          url: "/",
          roles: [
            "Admin",
            "Guard",
            "Guard Supervisor",
            "Dispatch",
            "Safety",
            "Mechanic",
            "NewMechanic",
          ],
          text: t("page-headers.inventory"),
        },
        {
          url: "/reservations",
          roles: ["Admin", "Dispatch"],
          text: t("page-headers.reservations"),
        },
        {
          url: "/pending-tasks",
          roles: ["Admin", "Safety", "Mechanic", "NewMechanic"],
          text: t("page-headers.tasks"),
        },
      ],
    },
    {
      category: t("page-headers.check-in-out"),
      roles: ["Admin", "Guard", "Guard Supervisor", "Safety"],
      icon: <CheckInIcon />,
      routes: [
        {
          url: "/check-in-trailer",
          roles: ["Admin", "Guard", "Safety", "Guard Supervisor"],
          text: t("page-headers.check-in"),
        },
        {
          url: "/check-out",
          roles: ["Admin", "Guard", "Guard Supervisor", "Safety"],
          text: t("page-headers.check-out"),
        },
      ],
    },

    {
      category: t("page-headers.reports"),
      roles: ["Admin", "Safety", "Mechanic", "Maintenance"],
      icon: <ReportsIcon />,
      routes: [
        {
          url: "/ctpat-reports",
          roles: ["Admin", "Safety"],
          text: "C-TPAT",
        },
        {
          url: "/export-jobs",
          roles: ["Admin", "Safety"],
          text: t("page-headers.exports"),
        },
      ],
    },
    {
      category: "Maintenance",
      roles: ["Admin", "Dispatch", "Safety"],
      icon: <InventoryIcon />,
      routes: [
        {
          url: "/inventory",
          roles: ["Admin", "Safety", "Mechanic", "Maintenance"],
          text: t("page-headers.inventory"),
        },
        {
          url: "/maintenance-reports",
          roles: ["Admin", "Safety", "Mechanic", "Dispatch", "Maintenance"],
          text: t("page-headers.repairs"),
        },
        {
          url: "/invoices",
          roles: ["Admin", "Safety"],
          text: t("page-headers.invoices"),
        },
        {
          url: "/receipts",
          roles: ["Admin", "Safety"],
          text: t("page-headers.receipts"),
        },
        {
          url: "/task-board",
          roles: ["Admin", "Safety"],
          text: t("page-headers.task-board"),
        },
      ],
    },
    {
      category: t("page-headers.resources"),
      roles: ["Admin", "Safety"],
      icon: <DriverWheel />,
      routes: [
        {
          url: "/employees",
          roles: ["Admin", "Safety"],
          text: t("page-headers.employees"),
        },
        {
          url: "/locations",
          roles: ["Admin", "Safety"],
          text: t("page-headers.locations"),
        },
        {
          url: "/seals",
          roles: ["Admin", "Safety"],
          text: t("page-headers.seals"),
        },
        {
          url: "/suppliers",
          roles: ["Admin", "Safety"],
          text: t("page-headers.suppliers"),
        },
                {
          url: "/trailer-assets",
          roles: ["Admin", "Safety"],
          text: t("page-headers.trailerAssets")
        },
        {
          url: "/users",
          roles: ["Admin"],
          text: t("page-headers.users"),
        }
      ],
    },
    {
      category: t("page-headers.backlog"),
      roles: ["Admin", "Safety", "Dispatch"],
      icon: <BacklogIcon2 />,
      routes: [
        {
          url: "/backlog",
          roles: ["Admin", "Safety", "Dispatch"],
          text: t("page-headers.backlog"),
        },
        {
          url: "/stock-logs",
          roles: ["Admin", "Safety"],
          text: t("page-headers.stock-logs"),
        },
        {
          url: "/tasks-history",
          roles: ["Admin", "Safety"],
          text: t("page-headers.tasks-history"),
        },
      ],
    },
  ];

  const filterNavRoutes = (navRoutes, user) => {
    const userGroups =
      (user && Array.isArray(user.groups) && user.groups) || [];
    return navRoutes
      .filter((category) => userHasAccess(category.roles, userGroups)) // Filter categories
      .map((category) => ({
        ...category,
        routes: category.routes.filter((route) =>
          userHasAccess(route.roles, userGroups),
        ), // Filter routes within the category
      }));
  };

  const handleOpenCategory = (category) => {
    if (openCategory === category) {
      setOpenCategory("");
    } else {
      setOpenCategory(category);
    }
  };

  useEffect(() => {
    try {
      if (user) {
        setIsAuth(true);
        setFilteredRoutes(filterNavRoutes(navRoutes, user));
      } else {
        setIsAuth(false);
        setFilteredRoutes([]); // clear when no user
      }
    } catch (e) {
      window.alert(`Error occured loading: ${e}`);
    }
  }, [user, i18n.language]); // recompute when language changes so t(...) updates are picked up

  return (
    <>
      {isAuth && user ? (
        isTablet ? (
          <div
            id="sidebar"
            className={`sidebar${
              isSideOpen ? "-active" : ""
            } sidebar-${user.theme.toLowerCase()}`}
          >
            <div className="sidebar-header">
              <a className="logo-a" id="logo-a" href="/">
                <Logo
                  fillColor={user.theme === "System" ? "#FFFF" : "#D43732"}
                />
                <h4 className="sidebar-name">Forward</h4>
              </a>
              <div onClick={openSideBar}>
                <OpenSidebarIcon
                  color={user.theme === "System" ? "#CFBEFF" : "#2EBCEE"}
                />
              </div>
            </div>
            <div className="side-menu">
              <ul className="menu">
                {filteredRoutes.map((item) =>
                  IsUserRole(item.roles) ? (
                    isSideOpen ? (
                      <NavCategory
                        item={item}
                        setOpenCategory={handleOpenCategory}
                        openCategory={openCategory}
                        user={user}
                      />
                    ) : (
                      <MobileNavCategory
                        item={item}
                        setOpenCategory={handleOpenCategory}
                        openCategory={openCategory}
                        user={user}
                        isSideOpen={isSideOpen}
                      />
                    )
                  ) : null,
                )}
              </ul>
            </div>
            <div className="side-menu logout">
              <ul className="menu">
                <NavRoute
                  setOpenCategory={() => setOpenCategory("")}
                  icon={<SettingsIcon />}
                  route={"/settings"}
                  text={t("page-headers.settings")}
                  user={user}
                />
                <NavRoute
                  setOpenCategory={() => setOpenCategory("")}
                  icon={<LogoutIcon />}
                  route={"/logout"}
                  text={t("page-headers.logout")}
                  user={user}
                />
              </ul>
            </div>
          </div>
        ) : isMobile ? (
          <div
            id="sidebar"
            className={`sidebar${
              isSideOpen ? "-active mobile open" : "-active mobile close"
            } sidebar-${user.theme.toLowerCase()}`}
          >
            <div className="sidebar-header">
              <a className="logo-a" id="logo-a" href="/">
                <Logo
                  fillColor={user.theme === "System" ? "#FFFF" : "#D43732"}
                />
                <h4 className="sidebar-name">Forward</h4>
              </a>
              <div onClick={openSideBar}>
                {isMobile ? (
                  <CheckOutIcon
                    color={user.theme === "System" ? "#CFBEFF" : "#2EBCEE"}
                  />
                ) : (
                  <OpenSidebarIcon
                    color={user.theme === "System" ? "#CFBEFF" : "#2EBCEE"}
                  />
                )}
              </div>
            </div>
            <div className="side-menu">
              <ul className="menu">
                {filteredRoutes.map((item, index) =>
                  IsUserRole(item.roles) ? (
                    <NavCategory
                      item={item}
                      setOpenCategory={handleOpenCategory}
                      openCategory={openCategory}
                      user={user}
                    />
                  ) : null,
                )}
              </ul>
            </div>
            <div className="side-menu logout">
              <ul className="menu">
                <NavRoute
                  setOpenCategory={() => setOpenCategory("")}
                  icon={<SettingsIcon />}
                  route={"/settings"}
                  text={t("page-headers.settings")}
                  user={user}
                />
                <NavRoute
                  setOpenCategory={() => setOpenCategory("")}
                  icon={<LogoutIcon />}
                  route={"/logout"}
                  text={t("page-headers.logout")}
                  user={user}
                />
              </ul>
            </div>
          </div>
        ) : (
          <div
            id="sidebar"
            className={`sidebar sidebar-${user.theme.toLowerCase()}`}
          >
            <div className="sidebar-header">
              <a className="logo-a" id="logo-a" href="/">
                <Logo
                  fillColor={user.theme === "System" ? "#FFFF" : "#D43732"}
                />
                <h4 className="sidebar-name">Forward</h4>
              </a>
              <div onClick={openSideBar}>
                <OpenSidebarIcon
                  color={user.theme === "System" ? "#CFBEFF" : "#2EBCEE"}
                />
              </div>
            </div>
            <div className="side-menu">
              <ul className="menu">
                {filteredRoutes.map((item, index) =>
                  IsUserRole(item.roles) ? (
                    <NavCategory
                      item={item}
                      setOpenCategory={handleOpenCategory}
                      openCategory={openCategory}
                      user={user}
                    />
                  ) : null,
                )}
              </ul>
            </div>
            <div className="side-menu logout">
              <ul className="menu">
                <NavRoute
                  setOpenCategory={() => setOpenCategory("")}
                  icon={<SettingsIcon />}
                  route={"/settings"}
                  text={t("page-headers.settings")}
                  user={user}
                />
                <NavRoute
                  setOpenCategory={() => setOpenCategory("")}
                  icon={<LogoutIcon />}
                  route={"/logout"}
                  text={t("page-headers.logout")}
                  user={user}
                />
              </ul>
            </div>
          </div>
        )
      ) : null}
    </>
  );
};
